import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import BsModal from 'ember-bootstrap/components/bs-modal';

interface AdminActionModalSignature<T> {
  Args: {
    title: string;
    submitLabel: string;
    submittingLabel: string;
    /** Defaults to `btn-danger` for destructive actions. */
    submitButtonClass?: string;
    initialValue: T;
    /** Omit to always allow submitting. */
    isValid?: (value: T) => boolean;
    /** Shown next to the submit button while it's disabled because @isValid says no. */
    invalidHint?: string;
    /**
     * Omit the built-in submit button, for fields that already have their own
     * commit affordance (the reschedule modal's Cronofy picker confirms in the
     * widget itself). Such fields submit via the yielded `submitValue` instead.
     */
    hideSubmitButton?: boolean;
    onSubmit: (value: T) => Promise<void>;
  };
  Blocks: {
    trigger: [showModal: () => void];
    description?: [];
    fields: [
      value: T,
      setValue: (value: T) => void,
      isSubmitting: boolean,
      submitValue: (value: T) => Promise<void>,
    ];
  };
}

/**
 * Shared shell for the admin session-action modals (cancel, abandon, …one
 * modal, one field, one submit handler each; everything else — the open/
 * close/submit state machine, the error alert, the button — lives here.
 */
export class AdminActionModal<T> extends Component<
  AdminActionModalSignature<T>
> {
  @tracked visible = false;
  @tracked isSubmitting = false;
  @tracked errorMessage: string | null = null;
  /** undefined = not yet touched since the modal last opened — falls back to @initialValue. */
  @tracked private _value: T | undefined = undefined;

  get value(): T {
    return this._value === undefined ? this.args.initialValue : this._value;
  }

  openModal = () => {
    this._value = undefined;
    this.errorMessage = null;
    this.visible = true;
  };

  closeModal = () => {
    this.visible = false;
  };

  setValue = (value: T) => {
    this._value = value;
  };

  get disableSubmit(): boolean {
    if (this.isSubmitting) {
      return true;
    }
    return this.args.isValid ? !this.args.isValid(this.value) : false;
  }

  get submitButtonClass(): string {
    return this.args.submitButtonClass ?? 'btn-danger';
  }

  get showInvalidHint(): boolean {
    return (
      !this.isSubmitting &&
      Boolean(this.args.isValid) &&
      !this.args.isValid!(this.value) &&
      Boolean(this.args.invalidHint)
    );
  }

  submit = async (event: SubmitEvent) => {
    event.preventDefault();
    if (this.disableSubmit) {
      return;
    }
    await this.runSubmit(this.value);
  };

  /**
   * Submit a value straight from the fields block, for fields that commit
   * themselves rather than waiting on the modal's own button.
   */
  submitValue = async (value: T) => {
    if (this.isSubmitting) {
      return;
    }
    this._value = value;
    await this.runSubmit(value);
  };

  private async runSubmit(value: T): Promise<void> {
    this.isSubmitting = true;
    this.errorMessage = null;

    try {
      await this.args.onSubmit(value);
      this.closeModal();
    } catch (error) {
      this.errorMessage =
        error instanceof Error ? error.message : 'Something went wrong.';
    } finally {
      this.isSubmitting = false;
    }
  }

  <template>
    {{yield this.openModal to="trigger"}}
    <BsModal
      @open={{this.visible}}
      @onHidden={{this.closeModal}}
      @size="lg"
      @renderInPlace={{true}}
      @backdropClose={{false}}
      as |modal|
    >
      <modal.header>
        <h2 class="modal-title h5">{{@title}}</h2>
      </modal.header>
      <modal.body>
        {{! Gated on @visible, not just @open's animation — fields with side
          effects (e.g. mounting a Cronofy Elements widget, fetching a token)
          should not run before the modal is actually shown. }}
        {{#if this.visible}}
          <form {{on "submit" this.submit}} data-test-admin-action-form>
            {{#if (has-block "description")}}
              <p class="text-body-secondary mb-3">{{yield to="description"}}</p>
            {{/if}}

            {{#if this.errorMessage}}
              <div class="alert alert-danger" role="alert">
                {{this.errorMessage}}
              </div>
            {{/if}}

            {{yield
              this.value
              this.setValue
              this.isSubmitting
              this.submitValue
              to="fields"
            }}

            {{#unless @hideSubmitButton}}
              <div class="d-flex align-items-center gap-2 mt-4">
                <button
                  type="submit"
                  class="btn {{this.submitButtonClass}}"
                  disabled={{this.disableSubmit}}
                  data-test-admin-action-submit
                >
                  {{if this.isSubmitting @submittingLabel @submitLabel}}
                </button>
                {{#if this.showInvalidHint}}
                  <span
                    class="text-body-secondary small"
                  >{{@invalidHint}}</span>
                {{/if}}
              </div>
            {{/unless}}
          </form>
        {{/if}}
      </modal.body>
    </BsModal>
  </template>
}
